// CreateVariantForm — field-staff variant creation embedded in the
// + Add Asset surface (buildspec 20-ui §5.8, PRD §7.3.3 / §6.5).
// Takes a pre-selected sub-system, a name, a unit of measure, and
// a grouping convention; dispatches `createFieldAddedVariant` via
// the frozen capture-client contract (buildspec 20-ui §6.1), which
// maps to `POST /capture/field-additions` (10-api.md §5.8).
//
// Two success paths per the API spec:
//   1. 201 Created (also 200 on idempotent retry of an existing
//      field-added row) → server returns the new/existing asset-
//      type row. Call `onCreated(newAssetTypeId)` so the caller
//      can immediately `POST /capture/items` for the surrounding
//      space.
//   2. 409 `already_exists` with `{existingAssetTypeId}` in
//      `err.body.details` — a PERMANENT asset type with the same
//      name already lives under this sub-system. Prompt the user
//      to adopt the existing id instead of creating a duplicate;
//      on confirm, call `onCreated(existingAssetTypeId)` directly
//      (no second POST required) per acceptance 30-acceptance.md
//      §2.10.
//
// NOT optimistic (buildspec 20-ui §6.5) — need the server-assigned
// id before the caller can add the variant to the current room.
//
// Visual contract: matches the field-design redesign pack — a
// dedicated card with a "← Back to search" link, a breadcrumb
// pill anchoring the variant to its taxonomy ("Saving under
// <System> > <Sub-system>"), a Name input with a 0/255 character
// counter, a native UoM select, a stack of grouping radio tiles
// with descriptive hints, and a sticky footer with Cancel +
// Create variant.

import { useCallback, useId, useState } from 'react'
import type { FormEvent, ReactElement } from 'react'

import {
  GROUPING_CONVENTION,
  SELECTABLE_UNIT_OF_MEASURE_VALUES,
  UNIT_OF_MEASURE,
} from '@citycare/shared'
import type { GroupingConvention, UnitOfMeasure } from '@citycare/shared'

import { ApiError } from '../../hooks/api'
import { Alert } from '../../ui/alert'

import type { CaptureClient } from '../client/capture-client.types'

import {
  CollisionPrompt,
  GroupingRadioTiles,
} from './create-variant-form-controls'

export interface CreateVariantFormProps {
  // Capture-client instance used to dispatch the mutation. Passed
  // in so the form is testable in isolation and so the Phase-3
  // adapter swap is invisible here (buildspec 20-ui §6).
  client: CaptureClient
  subSystemId: string
  subSystemName: string
  // Parent system name for the breadcrumb pill. Optional so older
  // callers still work — when missing, the pill collapses to just
  // the sub-system name. Production callers should always provide
  // it (the Add Asset dialog wires it through from the picker).
  systemName?: string
  // Pre-fills the Name input — used when the field-staff user
  // typed a query into the AddAssetDialog search box that returned
  // no matches and tapped "Create new asset". The user can still
  // edit the name before submitting.
  initialName?: string
  // Called with the server-authoritative asset-type id — either
  // the newly-created row's id (201/200 success) or the existing
  // permanent row's id (409 collision, after the user confirms
  // adoption).
  onCreated: (assetTypeId: string) => void
  onCancel: () => void
}

// Display labels for the variant-creation UoM select. Includes m³ so
// the label table stays complete for the UnitOfMeasure type (still a
// valid value at the DB/type level for legacy data), even though m³ is
// no longer offered as a selectable option here.
const UOM_LABEL: Record<UnitOfMeasure, string> = {
  [UNIT_OF_MEASURE.COUNT]: 'Count',
  [UNIT_OF_MEASURE.METRE]: 'm',
  [UNIT_OF_MEASURE.SQUARE_METRE]: 'm²',
  [UNIT_OF_MEASURE.CUBIC_METRE]: 'm³',
}

const MAX_NAME_LENGTH = 255

function extractId(value: unknown): string | null {
  if (value === null || typeof value !== 'object') return null
  const id = (value as Record<string, unknown>).id
  return typeof id === 'string' && id.length > 0 ? id : null
}

function extractExistingAssetTypeId(body: unknown): string | null {
  if (body === null || typeof body !== 'object') return null
  const details = (body as Record<string, unknown>).details
  if (details === null || typeof details !== 'object') return null
  const id = (details as Record<string, unknown>).existingAssetTypeId
  return typeof id === 'string' && id.length > 0 ? id : null
}

export function CreateVariantForm({
  client,
  subSystemId,
  subSystemName,
  systemName,
  initialName,
  onCreated,
  onCancel,
}: CreateVariantFormProps): ReactElement {
  const reactId = useId()
  const nameId = `${reactId}-name`
  const nameDescId = `${reactId}-name-desc`
  const nameErrorId = `${reactId}-name-error`
  const uomId = `${reactId}-uom`
  const uomLabelId = `${reactId}-uom-label`
  const groupingLegendId = `${reactId}-grouping-legend`

  const [name, setName] = useState(initialName ?? '')
  const [unitOfMeasure, setUnitOfMeasure] = useState<UnitOfMeasure>(
    UNIT_OF_MEASURE.COUNT,
  )
  const [groupingConvention, setGroupingConvention] =
    useState<GroupingConvention>(GROUPING_CONVENTION.PER_SPACE)
  const [submitting, setSubmitting] = useState(false)
  const [nameError, setNameError] = useState<string | undefined>(undefined)
  const [formError, setFormError] = useState<string | undefined>(undefined)
  const [collision, setCollision] = useState<
    { existingAssetTypeId: string; name: string } | null
  >(null)

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      if (submitting) return
      const trimmed = name.trim()
      if (trimmed.length === 0) {
        setNameError('Name is required.')
        return
      }
      if (trimmed.length > MAX_NAME_LENGTH) {
        setNameError(`Name must be ${MAX_NAME_LENGTH} characters or fewer.`)
        return
      }
      setNameError(undefined)
      setFormError(undefined)
      setSubmitting(true)
      try {
        const result = await client.dispatch({
          kind: 'createFieldAddedVariant',
          subSystemId,
          name: trimmed,
          unitOfMeasure,
          groupingConvention,
        })
        if (result.kind !== 'createFieldAddedVariant') {
          setFormError('Unexpected response from server. Please try again.')
          return
        }
        const assetTypeId = extractId(result.assetType)
        if (assetTypeId === null) {
          setFormError('Unexpected response from server. Please try again.')
          return
        }
        onCreated(assetTypeId)
      } catch (err) {
        if (
          err instanceof ApiError &&
          err.status === 409 &&
          err.code === 'already_exists'
        ) {
          const existingAssetTypeId = extractExistingAssetTypeId(err.body)
          if (existingAssetTypeId !== null) {
            setCollision({ existingAssetTypeId, name: trimmed })
            return
          }
        }
        const message =
          err instanceof ApiError
            ? err.message
            : 'Could not create variant. Please try again.'
        setFormError(message)
      } finally {
        setSubmitting(false)
      }
    },
    [
      client,
      groupingConvention,
      name,
      onCreated,
      subSystemId,
      submitting,
      unitOfMeasure,
    ],
  )

  const adoptExisting = useCallback(() => {
    if (collision === null) return
    onCreated(collision.existingAssetTypeId)
  }, [collision, onCreated])

  const dismissCollision = useCallback(() => {
    setCollision(null)
  }, [])

  if (collision !== null) {
    return (
      <CollisionPrompt
        headingId={`${reactId}-collision-heading`}
        collidedName={collision.name}
        subSystemName={subSystemName}
        onAdopt={adoptExisting}
        onDismiss={dismissCollision}
      />
    )
  }

  return (
    <form onSubmit={handleSubmit} className="flex h-full flex-col" noValidate>
      <header className="border-b border-cc-forest/10 px-6 pt-5 pb-4">
        {/* Disabled while a `createFieldAddedVariant` dispatch is in
            flight. Without this guard, a user on a flaky network
            can tap Back, believe they cancelled, and then have
            `onCreated` fire when the request finally resolves —
            silently auto-adding the variant they thought they
            backed out of. Cancel in the footer carries the same
            guard for the same reason. */}
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          className="-ml-1 inline-flex min-h-11 items-center gap-1.5 rounded-md px-1.5 py-1 text-[12px] text-cc-forest/60 hover:bg-cc-cream hover:text-cc-forest focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cc-forest disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:text-cc-forest/60"
        >
          <svg
            aria-hidden="true"
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
          >
            <path
              d="M7 2 L3 6 L7 10"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Back to search
        </button>
        <h3
          id={`${reactId}-heading`}
          className="mt-2 font-display text-[26px] leading-none text-cc-forest"
        >
          Create variant
        </h3>
        <p className="mt-1.5 text-[13px] text-cc-forest/60">
          Add a new asset type your team can reuse.
        </p>
        <div className="mt-4 inline-flex items-center gap-2 rounded-lg bg-cc-cream px-3 py-2 text-xs">
          <span className="text-cc-forest/55">Saving under</span>
          {systemName !== undefined && systemName.length > 0 ? (
            <>
              <span className="text-cc-forest/70">{systemName}</span>
              <svg
                aria-hidden="true"
                width="10"
                height="10"
                viewBox="0 0 10 10"
                fill="none"
                className="text-cc-forest/30"
              >
                <path
                  d="M3 1 L7 5 L3 9"
                  stroke="currentColor"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                />
              </svg>
            </>
          ) : null}
          <span className="font-medium text-cc-forest">
            <span className="sr-only">Creating under: </span>
            {subSystemName}
          </span>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto px-6 py-5">
        <div className="space-y-6">
          {formError !== undefined ? (
            <Alert variant="error" title="Could not create variant">
              {formError}
            </Alert>
          ) : null}

          <div>
            <div className="mb-2 flex items-baseline gap-1">
              <label
                htmlFor={nameId}
                className="text-[12px] font-medium uppercase tracking-[0.14em] text-cc-forest/70"
              >
                Name
              </label>
              <span aria-hidden="true" className="text-[12px] text-cc-orange">
                *
              </span>
            </div>
            <input
              id={nameId}
              type="text"
              value={name}
              onChange={(e) => {
                setName(e.target.value)
                if (nameError !== undefined) setNameError(undefined)
              }}
              maxLength={MAX_NAME_LENGTH}
              autoComplete="off"
              required
              aria-invalid={nameError !== undefined ? true : undefined}
              aria-describedby={`${nameDescId}${
                nameError !== undefined ? ` ${nameErrorId}` : ''
              }`}
              disabled={submitting}
              placeholder="e.g. Filter — Reverse osmosis"
              className={`block min-h-11 w-full rounded-xl border bg-white px-4 py-3 text-sm text-cc-forest placeholder:text-cc-forest/30 focus:outline-none disabled:cursor-not-allowed disabled:bg-cc-cream/40 ${
                nameError !== undefined
                  ? 'border-cc-orange focus:border-cc-orange'
                  : 'border-cc-forest/15 focus:border-cc-forest'
              }`}
            />
            <div className="mt-1.5 flex items-center justify-between gap-2">
              <span
                id={nameDescId}
                className="text-[11px] text-cc-forest/45"
              >
                Searchable across the asset register.
              </span>
              <span className="tabular-nums text-[11px] text-cc-forest/35">
                {name.length}/{MAX_NAME_LENGTH}
              </span>
            </div>
            {nameError !== undefined ? (
              <p
                id={nameErrorId}
                role="alert"
                className="mt-1.5 flex items-center gap-1 text-[11px] text-cc-orange"
              >
                <span aria-hidden="true">⚠</span> {nameError}
              </p>
            ) : null}
          </div>

          <div>
            <div className="mb-2 flex items-baseline gap-1">
              <label
                id={uomLabelId}
                htmlFor={uomId}
                className="text-[12px] font-medium uppercase tracking-[0.14em] text-cc-forest/70"
              >
                Unit of Measure
              </label>
              <span aria-hidden="true" className="text-[12px] text-cc-orange">
                *
              </span>
            </div>
            <div className="relative">
              <select
                id={uomId}
                value={unitOfMeasure}
                onChange={(event) =>
                  setUnitOfMeasure(event.target.value as UnitOfMeasure)
                }
                disabled={submitting}
                required
                className="block min-h-11 w-full appearance-none rounded-xl border border-cc-forest/15 bg-white px-4 py-3 pr-10 text-sm text-cc-forest focus:border-cc-forest focus:outline-none disabled:cursor-not-allowed disabled:bg-cc-cream/40"
              >
                {SELECTABLE_UNIT_OF_MEASURE_VALUES.map((value) => (
                  <option key={value} value={value}>
                    {UOM_LABEL[value]}
                  </option>
                ))}
              </select>
              <span
                aria-hidden="true"
                className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-cc-forest/50"
              >
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                  <path
                    d="M2 4 L5 7 L8 4"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </div>
          </div>

          <GroupingRadioTiles
            legendId={groupingLegendId}
            value={groupingConvention}
            onChange={setGroupingConvention}
            disabled={submitting}
          />
        </div>
      </div>

      <footer className="flex items-center justify-end gap-2 border-t border-cc-forest/10 bg-cc-cream/40 px-6 py-4">
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          className="inline-flex min-h-11 items-center rounded-pill px-5 py-2.5 text-[13px] font-medium text-cc-forest hover:bg-cc-forest/10 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cc-forest disabled:opacity-60"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={submitting}
          aria-busy={submitting || undefined}
          className="inline-flex min-h-11 items-center gap-2 rounded-pill bg-cc-forest px-5 py-2.5 text-[13px] font-medium text-cc-cream hover:bg-cc-teal focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cc-lime disabled:opacity-60"
        >
          {submitting ? (
            <svg
              aria-hidden="true"
              width="14"
              height="14"
              viewBox="0 0 16 16"
              className="animate-spin"
            >
              <circle
                cx="8"
                cy="8"
                r="6"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeOpacity="0.3"
                fill="none"
              />
              <path
                d="M8 2 A6 6 0 0 1 14 8"
                stroke="currentColor"
                strokeWidth="1.6"
                fill="none"
                strokeLinecap="round"
              />
            </svg>
          ) : null}
          {submitting ? 'Creating…' : 'Create variant'}
        </button>
      </footer>
    </form>
  )
}
